import { filter, of, share, take, tap } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { replaceTokens } from '@components/Utility/Template.js';
import { isCtrlKey, isMobile } from '@components/Utility/index.js';
import DIContainer from '@components/DependencyInjectionContainer.js';
export class UrlHandler {
    openExternal(url) {
        window.open(url, '_blank', 'noopener,noreferrer');
    }
    isExternalUrl(url) {
        const parsedUrl = new URL(url);
        return parsedUrl.hostname !== window.location.hostname;
    }
    absoluteURL(input) {
        const raw = (input || '').trim();
        if (!raw)
            return window.location.href;
        const origin = window.location.origin;
        const { protocol, host, hostname } = window.location;
        if (/^(https?|ftp|file|ws|wss|mailto|tel|data|javascript):/i.test(raw)) {
            return raw;
        }
        if (raw.startsWith('//')) {
            return protocol + raw;
        }
        if (raw.startsWith('/') || raw.startsWith('?') || raw.startsWith('#')) {
            return new URL(raw, origin).href;
        }
        const domainLike = /^[A-Za-z0-9.-]+\.[A-Za-z]{2,}(?::\d+)?(\/.*)?$/;
        if (domainLike.test(raw)) {
            const candidate = new URL('https://' + raw);
            const candidateHost = candidate.hostname;
            const currentParts = hostname.split('.');
            const candidateParts = candidateHost.split('.');
            const currentRoot = currentParts.length >= 2 ? currentParts.slice(-2).join('.') : hostname;
            const candidateRoot = candidateParts.length >= 2 ? candidateParts.slice(-2).join('.') : candidateHost;
            const isSameHost = candidate.host === host;
            const isSameSite = candidateHost === hostname ||
                candidateHost.endsWith('.' + hostname) ||
                currentRoot === candidateRoot;
            if (isSameHost || isSameSite) {
                const path = candidate.pathname + candidate.search + candidate.hash;
                return origin + path;
            }
            return candidate.href;
        }
        return new URL(raw, origin).href;
    }
    paramsFromUrlGet(url) {
        if (!url) {
            return { pathname: '', params: {}, hash: '', formname: '' };
        }
        try {
            const fullUrl = this.absoluteURL(url);
            const urlObj = new URL(fullUrl);
            const searchParams = urlObj.searchParams;
            const pathname = urlObj.pathname;
            const hash = urlObj.hash;
            const params = {};
            for (const [key, value] of searchParams) {
                params[key] = value;
            }
            let formname = this.extractLastPathSegment(pathname);
            if (!formname) {
                formname = params._form || '';
            }
            return { pathname, params, hash, formname };
        }
        catch (e) {
            return { pathname: '', params: {}, hash: '', formname: '' };
        }
    }
    extractLastPathSegment(path) {
        if (!path || path === '/') {
            return '';
        }
        const segments = path.split('/').filter(segment => segment.length > 0);
        return segments.pop() || '';
    }
    parseResqZoomForm(xmlString) {
        if (!xmlString) {
            return '';
        }
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
        const zoomFormXML = xmlDoc.getElementsByTagName('ZoomForm')[0];
        const getAttributeCaseInsensitive = (element, attributeName) => {
            for (const attr of element.attributes) {
                if (attr.name.toLowerCase() === attributeName.toLowerCase()) {
                    return attr.value;
                }
            }
            return '';
        };
        const webaction = getAttributeCaseInsensitive(zoomFormXML, 'webaction').toLowerCase();
        const webparamnames = getAttributeCaseInsensitive(zoomFormXML, 'webparamnames').toLowerCase().split(',');
        const webhash = getAttributeCaseInsensitive(zoomFormXML, 'webhash') || '';
        const webparamvaluefields = splitByCommasOutsideBrackets(getAttributeCaseInsensitive(zoomFormXML, 'webparamvaluefields') || '');
        if (!webparamnames || !webparamvaluefields) {
            return `/form/${webaction}`;
        }
        if (webparamnames.length !== webparamvaluefields.length) {
            throw new Error('webparamnames and webparamvaluefields must have the same number of elements.');
        }
        const queryString = webparamnames.map((param, index) => {
            let value = (webparamvaluefields[index] || '').trim();
            if (!value.startsWith('[') && !value.endsWith(']')) {
                value = `{{${value.toLowerCase()}}}`;
            }
            else if (value.startsWith('[') && value.endsWith(']')) {
                value = encodeURIComponent(value.slice(1, -1));
            }
            return `${param}=${value}`;
        }).join('&');
        return `/form/${webaction}?${queryString}${webhash ? '#' + webhash : ''}`;
    }
    convertLegacyQ360Url(url) {
        if (url.startsWith('controller.php?action=')) {
            url = url.replace('controller.php?action=', '/form/');
            url = url.replace('&', '?');
        }
        else if (url.indexOf('/controller.php?action=') > 0) {
            url = url.substring(url.indexOf('/controller.php?action='));
            url = url.replace('/controller.php?action=', '/form/');
            url = url.replace('&', '?');
        }
        return url;
    }
    zoom(urlTemplate, options) {
        urlTemplate = this.convertLegacyQ360Url(urlTemplate);
        let url = replaceTokens(urlTemplate, options?.data, { urlEncode: true });
        url = this.absoluteURL(url);
        if (/^(tel|mailto|sms):/i.test(url)) {
            DIContainer.getInstance('Application').suppressBeforeUnload = true;
            setTimeout(() => { DIContainer.getInstance('Application').suppressBeforeUnload = false; }, 1000);
            window.location.href = url;
            return of(true);
        }
        const externalUrl = this.isExternalUrl(url);
        let urlTarget = options?.target || '';
        const ctrlKey = options?.event && isCtrlKey(options.event);
        const shiftKey = options?.event?.shiftKey;
        if (ctrlKey && shiftKey) {
            urlTarget = '_self';
        }
        else if (ctrlKey || shiftKey) {
            urlTarget = '_blank';
        }
        const openNewBrowserTab = externalUrl || urlTarget === '_blank';
        const parsedUrl = this.paramsFromUrlGet(url);
        const trackNavigation = (target) => {
            DIContainer.getInstance('Application').trackTelemetry({
                type: 'form_navigate',
                componentId: options?.initiator?.id || '',
                formName: options?.initiator?.formContext?.formName || '',
                metadata: {
                    url: parsedUrl.pathname,
                    target,
                    destinationForm: parsedUrl.formname || ''
                }
            });
        };
        if (externalUrl) {
            const obs = DIContainer.getInstance('CodeTypeManager').getCodes('URLWHTLIST').pipe(map(codes => {
                const urlDomain = new URL(url).hostname.replace(/^www\./, '').toLowerCase();
                return codes.some(code => {
                    const codeValue = code.codevalue.toLowerCase();
                    return urlDomain === codeValue || urlDomain.endsWith('.' + codeValue);
                });
            }), switchMap(isWhitelisted => {
                if (isWhitelisted) {
                    return of(true);
                }
                else {
                    return DIContainer.getInstance('Application').confirm({
                        caption: 'You are about to leave Q360 to an external website. \r\n\r\n' + url,
                        modalStyle: 'warning'
                    });
                }
            }), tap(confirmed => {
                if (confirmed) {
                    trackNavigation('external');
                    this.openExternal(url);
                }
            }), share());
            obs.subscribe();
            return obs;
        }
        else if (openNewBrowserTab) {
            trackNavigation('new_tab');
            const obs = of(true);
            obs.pipe(tap(() => {
                if (shiftKey) {
                    window.open(url, '_blank', 'noopener,noreferrer');
                }
                else {
                    window.open(url);
                }
            })).subscribe();
            return obs;
        }
        else {
            if (isMobile()) {
                // panels cover the full screen on mobile, so if they clicked a notification item or hyperlink in the AI chat, or a global search result, we need to hide the panel to see the form
                DIContainer.getInstance('Application')?.closeActivePanel();
            }
            let form;
            if (options?.initiator) {
                form = DIContainer.getInstance('ComponentRegistry').getFormComponentByElement(options.initiator.rootElement);
            }
            if (urlTarget === '_self' && !form && options?.recordNavigationDetail && options?.recordNavigationDetail.formId) {
                form = DIContainer.getInstance('ComponentRegistry').getComponent(options.recordNavigationDetail.formId);
            }
            let newWindow = urlTarget === '_top' || !form;
            if (!urlTarget) {
                if (!form || url.indexOf(`/form/${form.propertyGet('formName')}`) > -1) {
                    newWindow = true;
                }
                else {
                    newWindow = true;
                }
            }
            trackNavigation(newWindow ? 'new_form' : 'same_form');
            let obs;
            if (newWindow) {
                const screen = DIContainer.getInstance('ScreenManager').open(url, {
                    title: '',
                    delayUntilRefreshed: !!options?.state
                });
                obs = screen.propertyGet$('currentForm')
                    .pipe(filter((form) => !!form));
            }
            else if (form) {
                obs = form.navigate(url);
            }
            const setFormState = (form, state) => {
                form.records.main.state = state;
                form.updateTitle();
            };
            if (obs) {
                if (options?.recordNavigationDetail) {
                    obs.pipe(tap((form) => {
                        options.recordNavigationDetail.formId = form.globalId;
                        form?.recordNavigationDetailSet(options?.recordNavigationDetail);
                    }), share()).subscribe();
                }
                if (options?.state) {
                    obs.pipe(tap((form) => {
                        setFormState(form, options.state);
                        if (options.state === 'add' && options.revertToken) {
                            form.records.main.revertToken = options.revertToken;
                        }
                    }), share()).subscribe();
                }
                return obs.pipe(map((form) => !!form), take(1));
            }
            return of(false);
        }
    }
}
function splitByCommasOutsideBrackets(input, stripBrackets = false) {
    const out = [];
    let buf = '';
    let depth = 0;
    for (let i = 0; i < input.length; i++) {
        const ch = input[i];
        if (ch === '[') {
            depth++;
            buf += ch;
        }
        else if (ch === ']') {
            depth = Math.max(0, depth - 1);
            buf += ch;
        }
        else if (ch === ',' && depth === 0) {
            const token = buf.trim();
            if (token) {
                out.push(stripBrackets && token.startsWith('[') && token.endsWith(']')
                    ? token.slice(1, -1)
                    : token);
            }
            buf = '';
        }
        else {
            buf += ch;
        }
    }
    const finalToken = buf.trim();
    if (finalToken) {
        out.push(stripBrackets && finalToken.startsWith('[') && finalToken.endsWith(']')
            ? finalToken.slice(1, -1)
            : finalToken);
    }
    return out;
}
